import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Users,
  Store,
  Package,
  Plus,
  Search,
  MoreVertical,
  Check,
  X,
  Mail,
  Phone,
  Building2,
  Settings,
  BarChart3,
  RefreshCw,
  UserPlus,
  Trash2,
  Eye,
  Edit,
  FileText,
  Download,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getErrorMessage } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface AssociationMember {
  id: string;
  user_id: string;
  company_id: string;
  association_id: string;
  member_code?: string;
  membership_type: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  company?: {
    name: string;
    email?: string;
    phone?: string;
    city?: string;
  };
}

interface AssociationSeller {
  id: string;
  user_id: string;
  company_id: string;
  association_id: string;
  seller_code?: string;
  categories: string[];
  is_active: boolean;
  is_verified: boolean;
  is_preferred: boolean;
  created_at: string;
  company?: {
    name: string;
    email?: string;
    phone?: string;
    city?: string;
  };
}

interface Association {
  id: string;
  name: string;
  slug: string;
  description?: string;
  email?: string;
  modules: {
    groupage: boolean;
    supplier_directory: boolean;
    ai_chat: boolean;
    analytics: boolean;
    stock_management: boolean;
  };
  settings: {
    primary_color: string;
    allow_member_proposals: boolean;
    default_collection_days: number;
    min_participants: number;
  };
  stats?: {
    total_members: number;
    active_members: number;
    total_sellers: number;
    active_sellers: number;
    total_groupages: number;
    active_groupages: number;
  };
}

interface GroupageOpportunity {
  id: string;
  title: string;
  seller_company_name: string;
  status: string;
  collection_end: string;
  participants_count: number;
  total_orders: number;
  total_value: number;
}

const AssociationDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [association, setAssociation] = useState<Association | null>(null);
  const [members, setMembers] = useState<AssociationMember[]>([]);
  const [sellers, setSellers] = useState<AssociationSeller[]>([]);
  const [groupages, setGroupages] = useState<GroupageOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Search states
  const [memberSearch, setMemberSearch] = useState('');
  const [sellerSearch, setSellerSearch] = useState('');
  
  // Dialog states
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showAddSellerDialog, setShowAddSellerDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showCreateGroupageDialog, setShowCreateGroupageDialog] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newSellerEmail, setNewSellerEmail] = useState('');
  
  // Opzioni groupage del tenant
  const [tenantGroupageOptions, setTenantGroupageOptions] = useState<{
    stock_limits_enabled: boolean;
    default_collection_days: number;
  } | null>(null);
  const [loadingGroupageOptions, setLoadingGroupageOptions] = useState(true);
  
  // Create Groupage form
  const [groupageForm, setGroupageForm] = useState({
    title: '',
    description: '',
    seller_company_id: '',
    collection_type: 'days' as 'days' | 'date',  // Tipo chiusura
    collection_days: 7,  // Giorni apertura
    collection_end_date: '',  // Data chiusura fissa
    min_participants: 1,
    shipping_cost_type: 'included' as 'included' | 'fixed' | 'per_order',
    shipping_cost: 0,
    payment_notes: '',
    stock_enabled: false  // Toggle opzioni stock
  });
  const [sellerProducts, setSellerProducts] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Record<string, { 
    selected: boolean; 
    unit_price: number; 
    min_quantity: number;
    max_quantity_per_buyer?: number;
    max_quantity_total?: number;
  }>>({});
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [creatingGroupage, setCreatingGroupage] = useState(false);

  useEffect(() => {
    if (user?.association_id && user?.is_association_admin) {
      fetchAssociationData();
      fetchTenantGroupageOptions();
    }
  }, [user?.association_id, user?.is_association_admin]);

  const fetchTenantGroupageOptions = async () => {
    setLoadingGroupageOptions(true);
    try {
      const res = await axios.get(`${API}/associations/my-groupage-options`);
      if (res.data.groupage_options) {
        setTenantGroupageOptions(res.data.groupage_options);
      } else {
        setTenantGroupageOptions({ stock_limits_enabled: false, default_collection_days: 7 });
      }
    } catch {
      // Default options
      setTenantGroupageOptions({ stock_limits_enabled: false, default_collection_days: 7 });
    } finally {
      setLoadingGroupageOptions(false);
    }
  };

  const fetchAssociationData = async () => {
    if (!user?.association_id) return;
    
    try {
      setLoading(true);
      
      // Fetch association details with stats
      const assocRes = await axios.get(`${API}/associations/${user.association_id}`);
      setAssociation(assocRes.data);
      
      // Fetch members
      const membersRes = await axios.get(`${API}/associations/${user.association_id}/members`);
      setMembers(membersRes.data.members || []);
      
      // Fetch sellers
      const sellersRes = await axios.get(`${API}/associations/${user.association_id}/sellers`);
      setSellers(sellersRes.data.sellers || []);
      
      // Fetch groupages
      const groupagesRes = await axios.get(`${API}/groupage/opportunities?association_id=${user.association_id}`);
      setGroupages(groupagesRes.data.opportunities || []);
      
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMemberEmail || !user?.association_id) return;
    
    try {
      // First find user by email
      const usersRes = await axios.get(`${API}/admin/all-companies`);
      const companies = usersRes.data;
      const buyerCompany = companies.find((c: any) => c.type === 'buyer' && c.email === newMemberEmail);
      
      if (!buyerCompany) {
        toast.error('Utente non trovato. Assicurati che sia registrato come buyer.');
        return;
      }
      
      // Find user for this company
      const userToAdd = await axios.get(`${API}/company/users`);
      
      await axios.post(`${API}/associations/${user.association_id}/members?user_id=${buyerCompany.owner_id}`);
      toast.success('Membro aggiunto');
      setShowAddMemberDialog(false);
      setNewMemberEmail('');
      fetchAssociationData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleMemberStatus = async (memberId: string, currentStatus: boolean) => {
    try {
      // For now just show toast - implement endpoint later
      toast.info('Funzionalità in sviluppo');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleSellerStatus = async (sellerId: string, currentStatus: boolean) => {
    try {
      toast.info('Funzionalità in sviluppo');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Fetch seller products when seller is selected
  const handleSellerChange = async (sellerId: string) => {
    setGroupageForm({ ...groupageForm, seller_company_id: sellerId });
    setSelectedProducts({});
    
    if (!sellerId) {
      setSellerProducts([]);
      return;
    }
    
    setLoadingProducts(true);
    try {
      const res = await axios.get(`${API}/products/catalog/${sellerId}`);
      const products = res.data.products || res.data || [];
      setSellerProducts(products);
    } catch (error) {
      toast.error('Errore nel caricamento dei prodotti');
      setSellerProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Toggle product selection
  const toggleProductSelection = (productId: string, product: any) => {
    setSelectedProducts(prev => {
      if (prev[productId]?.selected) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [productId]: {
          selected: true,
          unit_price: product.price || 0,
          min_quantity: 1
        }
      };
    });
  };

  // Create Groupage
  const handleCreateGroupage = async () => {
    if (!groupageForm.title || !groupageForm.seller_company_id) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    // Valida data chiusura se tipo "date"
    if (groupageForm.collection_type === 'date' && !groupageForm.collection_end_date) {
      toast.error('Seleziona una data di chiusura');
      return;
    }
    
    const selectedProductsList = Object.entries(selectedProducts)
      .filter(([_, data]) => data.selected)
      .map(([productId, data]) => {
        const product = sellerProducts.find(p => p.id === productId);
        return {
          product_id: productId,
          product_name: product?.name || '',
          product_sku: product?.sku || '',
          unit: product?.unit || 'pz',
          unit_price: data.unit_price,
          min_quantity: data.min_quantity,
          // Campi stock solo se abilitati
          ...(groupageForm.stock_enabled && data.max_quantity_per_buyer && {
            max_quantity_per_buyer: data.max_quantity_per_buyer
          }),
          ...(groupageForm.stock_enabled && data.max_quantity_total && {
            max_quantity_total: data.max_quantity_total
          })
        };
      });
    
    if (selectedProductsList.length === 0) {
      toast.error('Seleziona almeno un prodotto');
      return;
    }
    
    setCreatingGroupage(true);
    
    try {
      const now = new Date();
      let collectionEnd: Date;
      
      if (groupageForm.collection_type === 'date') {
        collectionEnd = new Date(groupageForm.collection_end_date);
      } else {
        collectionEnd = new Date(now.getTime() + groupageForm.collection_days * 24 * 60 * 60 * 1000);
      }
      
      await axios.post(`${API}/groupage/opportunities`, {
        title: groupageForm.title,
        description: groupageForm.description,
        seller_company_id: groupageForm.seller_company_id,
        products: selectedProductsList,
        collection_start: now.toISOString(),
        collection_end: collectionEnd.toISOString(),
        collection_type: groupageForm.collection_type,
        collection_days: groupageForm.collection_type === 'days' ? groupageForm.collection_days : undefined,
        min_participants: groupageForm.min_participants,
        shipping_cost_type: groupageForm.shipping_cost_type,
        shipping_cost: groupageForm.shipping_cost,
        payment_notes: groupageForm.payment_notes,
        payment_methods: ['bank_transfer'],
        stock_enabled: groupageForm.stock_enabled
      });
      
      toast.success('Groupage creato con successo!');
      setShowCreateGroupageDialog(false);
      resetGroupageForm();
      fetchAssociationData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCreatingGroupage(false);
    }
  };

  const resetGroupageForm = () => {
    setGroupageForm({
      title: '',
      description: '',
      seller_company_id: '',
      collection_type: 'days',
      collection_days: tenantGroupageOptions?.default_collection_days || 7,
      collection_end_date: '',
      min_participants: 1,
      shipping_cost_type: 'included',
      shipping_cost: 0,
      payment_notes: '',
      stock_enabled: false
    });
    setSellerProducts([]);
    setSelectedProducts({});
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      published: 'bg-green-500 text-white',
      collecting: 'bg-blue-500 text-white',
      closed: 'bg-gray-500 text-white',
      completed: 'bg-purple-500 text-white',
      draft: 'bg-yellow-500 text-white',
      pending_approval: 'bg-orange-500 text-white'
    };
    const labels: Record<string, string> = {
      published: 'Pubblicato',
      collecting: 'Raccolta',
      closed: 'Chiuso',
      completed: 'Completato',
      draft: 'Bozza',
      pending_approval: 'In Approvazione'
    };
    return <Badge className={styles[status] || 'bg-gray-500'}>{labels[status] || status}</Badge>;
  };

  // Filters
  const filteredMembers = members.filter(m => 
    m.company?.name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.member_code?.toLowerCase().includes(memberSearch.toLowerCase())
  );
  
  const filteredSellers = sellers.filter(s =>
    s.company?.name?.toLowerCase().includes(sellerSearch.toLowerCase()) ||
    s.seller_code?.toLowerCase().includes(sellerSearch.toLowerCase())
  );

  if (!user?.is_association_admin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Building2 className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Accesso Riservato</h2>
        <p className="text-muted-foreground">
          Questa pagina è riservata agli amministratori dell'associazione.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="association-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{association?.name || 'Gestione Associazione'}</h1>
          <p className="text-muted-foreground">
            Dashboard amministrativa
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSettingsDialog(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Impostazioni
          </Button>
          <Button variant="outline" onClick={fetchAssociationData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Membri</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{association?.stats?.active_members || members.length}</div>
            <p className="text-xs text-muted-foreground">
              {association?.stats?.total_members || members.length} totali
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fornitori</CardTitle>
            <Store className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{association?.stats?.active_sellers || sellers.length}</div>
            <p className="text-xs text-muted-foreground">
              {association?.stats?.total_sellers || sellers.length} totali
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Groupage Attivi</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {groupages.filter(g => ['published', 'collecting'].includes(g.status)).length}
            </div>
            <p className="text-xs text-muted-foreground">
              {association?.stats?.total_groupages || groupages.length} totali
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valore Totale</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              € {groupages.reduce((acc, g) => acc + g.total_value, 0).toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground">da tutti i Groupage</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Panoramica</TabsTrigger>
          <TabsTrigger value="members">
            Membri
            <Badge variant="secondary" className="ml-2">{members.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="sellers">
            Fornitori
            <Badge variant="secondary" className="ml-2">{sellers.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="groupages">Groupage</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Recent Members */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ultimi Membri</CardTitle>
              </CardHeader>
              <CardContent>
                {members.slice(0, 5).map(member => (
                  <div key={member.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <div className="font-medium">{member.company?.name || 'N/A'}</div>
                      <div className="text-sm text-muted-foreground">{member.company?.city}</div>
                    </div>
                    <Badge variant={member.is_active ? 'default' : 'secondary'}>
                      {member.is_active ? 'Attivo' : 'Inattivo'}
                    </Badge>
                  </div>
                ))}
                {members.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">Nessun membro</p>
                )}
              </CardContent>
            </Card>

            {/* Active Groupages */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Groupage Attivi</CardTitle>
              </CardHeader>
              <CardContent>
                {groupages.filter(g => ['published', 'collecting'].includes(g.status)).slice(0, 5).map(groupage => (
                  <div key={groupage.id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <div className="font-medium">{groupage.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {groupage.participants_count} partecipanti • € {groupage.total_value.toFixed(2)}
                      </div>
                    </div>
                    {getStatusBadge(groupage.status)}
                  </div>
                ))}
                {groupages.filter(g => ['published', 'collecting'].includes(g.status)).length === 0 && (
                  <p className="text-muted-foreground text-center py-4">Nessun Groupage attivo</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Modules Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Moduli Attivi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {association?.modules.groupage && (
                  <Badge className="bg-green-100 text-green-800">Groupage</Badge>
                )}
                {association?.modules.supplier_directory && (
                  <Badge className="bg-blue-100 text-blue-800">Directory Fornitori</Badge>
                )}
                {association?.modules.ai_chat && (
                  <Badge className="bg-purple-100 text-purple-800">AI Chat</Badge>
                )}
                {association?.modules.analytics && (
                  <Badge className="bg-orange-100 text-orange-800">Analytics</Badge>
                )}
                {association?.modules.stock_management && (
                  <Badge className="bg-yellow-100 text-yellow-800">Gestione Stock</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca membri..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={() => setShowAddMemberDialog(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Aggiungi Membro
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Azienda</TableHead>
                  <TableHead>Codice</TableHead>
                  <TableHead>Città</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Data Iscrizione</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map(member => (
                  <TableRow key={member.id}>
                    <TableCell className="font-medium">{member.company?.name || 'N/A'}</TableCell>
                    <TableCell>{member.member_code || '-'}</TableCell>
                    <TableCell>{member.company?.city || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{member.membership_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.is_active ? 'default' : 'secondary'}>
                        {member.is_active ? 'Attivo' : 'Inattivo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {format(parseISO(member.created_at), 'd MMM yyyy', { locale: it })}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleToggleMemberStatus(member.id, member.is_active)}>
                            {member.is_active ? (
                              <><X className="h-4 w-4 mr-2" /> Disattiva</>
                            ) : (
                              <><Check className="h-4 w-4 mr-2" /> Attiva</>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredMembers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Nessun membro trovato
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Sellers Tab */}
        <TabsContent value="sellers" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca fornitori..."
                value={sellerSearch}
                onChange={(e) => setSellerSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={() => setShowAddSellerDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi Fornitore
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Azienda</TableHead>
                  <TableHead>Codice</TableHead>
                  <TableHead>Categorie</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Preferito</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSellers.map(seller => (
                  <TableRow key={seller.id}>
                    <TableCell className="font-medium">{seller.company?.name || 'N/A'}</TableCell>
                    <TableCell>{seller.seller_code || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(seller.categories || []).slice(0, 2).map(cat => (
                          <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
                        ))}
                        {(seller.categories || []).length > 2 && (
                          <Badge variant="outline" className="text-xs">+{seller.categories.length - 2}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={seller.is_active ? 'default' : 'secondary'}>
                        {seller.is_active ? 'Attivo' : 'Inattivo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {seller.is_preferred && <Badge className="bg-yellow-100 text-yellow-800">Preferito</Badge>}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleToggleSellerStatus(seller.id, seller.is_active)}>
                            {seller.is_active ? 'Disattiva' : 'Attiva'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredSellers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Nessun fornitore trovato
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Groupages Tab */}
        <TabsContent value="groupages" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Tutti i Groupage</h3>
            <Button onClick={() => setShowCreateGroupageDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Groupage
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {groupages.map(groupage => (
              <Card key={groupage.id}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-base">{groupage.title}</CardTitle>
                    {getStatusBadge(groupage.status)}
                  </div>
                  <CardDescription>{groupage.seller_company_name}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Partecipanti</span>
                      <span className="font-medium">{groupage.participants_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ordini</span>
                      <span className="font-medium">{groupage.total_orders}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Valore Totale</span>
                      <span className="font-medium">€ {groupage.total_value.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Chiusura</span>
                      <span className="font-medium">
                        {format(parseISO(groupage.collection_end), 'd MMM yyyy', { locale: it })}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1">
                      <Eye className="h-4 w-4 mr-1" />
                      Dettagli
                    </Button>
                    {['closed', 'completed'].includes(groupage.status) && (
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {groupages.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Package className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nessun Groupage creato</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Membro</DialogTitle>
            <DialogDescription>
              Inserisci l'email del buyer da aggiungere all'associazione
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="member-email">Email</Label>
              <Input
                id="member-email"
                type="email"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                placeholder="email@azienda.it"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMemberDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleAddMember} disabled={!newMemberEmail}>
              Aggiungi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Impostazioni Associazione</DialogTitle>
            <DialogDescription>
              Configura le impostazioni dell'associazione
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Proposte dai Membri</Label>
                <p className="text-sm text-muted-foreground">
                  Permetti ai membri di proporre Groupage
                </p>
              </div>
              <Switch checked={association?.settings.allow_member_proposals} />
            </div>
            <div className="space-y-2">
              <Label>Giorni Default Raccolta</Label>
              <Input
                type="number"
                value={association?.settings.default_collection_days || 7}
                readOnly
              />
            </div>
            <div className="space-y-2">
              <Label>Partecipanti Minimi</Label>
              <Input
                type="number"
                value={association?.settings.min_participants || 1}
                readOnly
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Groupage Dialog */}
      <Dialog open={showCreateGroupageDialog} onOpenChange={(open) => {
        setShowCreateGroupageDialog(open);
        if (!open) resetGroupageForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Nuovo Groupage
            </DialogTitle>
            <DialogDescription>
              Crea una nuova opportunità di acquisto collettivo per i membri
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Info Base */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="groupage-title">Titolo *</Label>
                <Input
                  id="groupage-title"
                  value={groupageForm.title}
                  onChange={(e) => setGroupageForm({ ...groupageForm, title: e.target.value })}
                  placeholder="es. Offerta Formaggi Primavera 2026"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="groupage-description">Descrizione</Label>
                <Textarea
                  id="groupage-description"
                  value={groupageForm.description}
                  onChange={(e) => setGroupageForm({ ...groupageForm, description: e.target.value })}
                  placeholder="Descrivi l'opportunità di acquisto..."
                  rows={2}
                />
              </div>
            </div>

            {/* Seller Selection */}
            <div className="space-y-2">
              <Label>Fornitore *</Label>
              {sellers.length === 0 ? (
                <div className="border rounded-lg p-4 bg-yellow-50 text-yellow-800 text-sm">
                  <AlertCircle className="h-4 w-4 inline mr-2" />
                  Nessun fornitore associato. Vai alla tab "Fornitori" per aggiungerne uno.
                </div>
              ) : (
                <Select
                  value={groupageForm.seller_company_id}
                  onValueChange={handleSellerChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona fornitore" />
                  </SelectTrigger>
                  <SelectContent>
                    {sellers.filter(s => s.is_active).map(seller => (
                      <SelectItem key={seller.company_id} value={seller.company_id}>
                        {seller.company?.name || 'N/A'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Products Selection */}
            {groupageForm.seller_company_id && (
              <div className="space-y-2">
                <Label>Prodotti</Label>
                {loadingProducts ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : sellerProducts.length === 0 ? (
                  <div className="border rounded-lg p-4 bg-gray-50 text-gray-600 text-sm text-center">
                    Nessun prodotto disponibile per questo fornitore
                  </div>
                ) : (
                  <div className="border rounded-lg max-h-60 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Prodotto</TableHead>
                          <TableHead className="w-24">Prezzo</TableHead>
                          <TableHead className="w-20">Min. Qty</TableHead>
                          {groupageForm.stock_enabled && (
                            <>
                              <TableHead className="w-24">Max/Buyer</TableHead>
                              <TableHead className="w-24">Stock Tot.</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sellerProducts.map(product => (
                          <TableRow 
                            key={product.id}
                            className={selectedProducts[product.id]?.selected ? 'bg-primary/5' : ''}
                          >
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedProducts[product.id]?.selected || false}
                                onChange={() => toggleProductSelection(product.id, product)}
                                className="h-4 w-4"
                              />
                            </TableCell>
                            <TableCell className="font-medium">{product.name}</TableCell>
                            <TableCell>
                              {selectedProducts[product.id]?.selected ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="h-8 w-20"
                                  value={selectedProducts[product.id]?.unit_price || 0}
                                  onChange={(e) => setSelectedProducts(prev => ({
                                    ...prev,
                                    [product.id]: {
                                      ...prev[product.id],
                                      unit_price: parseFloat(e.target.value) || 0
                                    }
                                  }))}
                                />
                              ) : (
                                <span className="text-muted-foreground">€ {(product.price || 0).toFixed(2)}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {selectedProducts[product.id]?.selected && (
                                <Input
                                  type="number"
                                  min="1"
                                  className="h-8 w-16"
                                  value={selectedProducts[product.id]?.min_quantity || 1}
                                  onChange={(e) => setSelectedProducts(prev => ({
                                    ...prev,
                                    [product.id]: {
                                      ...prev[product.id],
                                      min_quantity: parseInt(e.target.value) || 1
                                    }
                                  }))}
                                />
                              )}
                            </TableCell>
                            {groupageForm.stock_enabled && selectedProducts[product.id]?.selected && (
                              <>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    placeholder="∞"
                                    className="h-8 w-20"
                                    value={selectedProducts[product.id]?.max_quantity_per_buyer || ''}
                                    onChange={(e) => setSelectedProducts(prev => ({
                                      ...prev,
                                      [product.id]: {
                                        ...prev[product.id],
                                        max_quantity_per_buyer: e.target.value ? parseInt(e.target.value) : undefined
                                      }
                                    }))}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    placeholder="∞"
                                    className="h-8 w-20"
                                    value={selectedProducts[product.id]?.max_quantity_total || ''}
                                    onChange={(e) => setSelectedProducts(prev => ({
                                      ...prev,
                                      [product.id]: {
                                        ...prev[product.id],
                                        max_quantity_total: e.target.value ? parseInt(e.target.value) : undefined
                                      }
                                    }))}
                                  />
                                </TableCell>
                              </>
                            )}
                            {groupageForm.stock_enabled && !selectedProducts[product.id]?.selected && (
                              <>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {Object.values(selectedProducts).filter(p => p.selected).length} prodotti selezionati
                </p>
              </div>
            )}

            {/* Collection Settings */}
            <div className="space-y-4 border-t pt-4">
              <Label className="text-base font-medium">Periodo di Raccolta</Label>
              
              {/* Tipo chiusura */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="collection_type"
                    checked={groupageForm.collection_type === 'days'}
                    onChange={() => setGroupageForm({ ...groupageForm, collection_type: 'days' })}
                    className="w-4 h-4 text-primary"
                  />
                  <span>Giorni apertura</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="collection_type"
                    checked={groupageForm.collection_type === 'date'}
                    onChange={() => setGroupageForm({ ...groupageForm, collection_type: 'date' })}
                    className="w-4 h-4 text-primary"
                  />
                  <span>Data chiusura</span>
                </label>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {groupageForm.collection_type === 'days' ? (
                  <div className="space-y-2">
                    <Label htmlFor="collection-days">Giorni di Apertura</Label>
                    <Input
                      id="collection-days"
                      type="number"
                      min="1"
                      max="365"
                      value={groupageForm.collection_days}
                      onChange={(e) => setGroupageForm({ 
                        ...groupageForm, 
                        collection_days: Math.max(1, parseInt(e.target.value) || 7) 
                      })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Chiusura: {new Date(Date.now() + groupageForm.collection_days * 24 * 60 * 60 * 1000).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="collection-end-date">Data di Chiusura</Label>
                    <Input
                      id="collection-end-date"
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={groupageForm.collection_end_date}
                      onChange={(e) => setGroupageForm({ 
                        ...groupageForm, 
                        collection_end_date: e.target.value 
                      })}
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="min-participants">Partecipanti Minimi</Label>
                  <Input
                    id="min-participants"
                    type="number"
                    min="1"
                    value={groupageForm.min_participants}
                    onChange={(e) => setGroupageForm({ 
                      ...groupageForm, 
                      min_participants: Math.max(1, parseInt(e.target.value) || 1) 
                    })}
                  />
                </div>
              </div>
            </div>

            {/* Stock Options (solo se abilitato nel tenant) */}
            {loadingGroupageOptions && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Caricamento opzioni...</span>
                </div>
              </div>
            )}
            
            {!loadingGroupageOptions && tenantGroupageOptions?.stock_limits_enabled && (
              <div className="space-y-4 border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Limiti Quantità</Label>
                    <p className="text-sm text-muted-foreground">
                      Abilita limiti per buyer e stock totale
                    </p>
                  </div>
                  <Switch
                    checked={groupageForm.stock_enabled}
                    onCheckedChange={(checked) => setGroupageForm({ 
                      ...groupageForm, 
                      stock_enabled: checked 
                    })}
                  />
                </div>
                
                {groupageForm.stock_enabled && (
                  <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Configura i limiti per ogni prodotto selezionato nella tabella sopra, oppure imposta un default:
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Max per Buyer (default)</Label>
                        <Input
                          type="number"
                          min="0"
                          placeholder="Illimitato"
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || undefined;
                            // Applica a tutti i prodotti selezionati
                            setSelectedProducts(prev => {
                              const updated = { ...prev };
                              Object.keys(updated).forEach(key => {
                                if (updated[key].selected) {
                                  updated[key] = { ...updated[key], max_quantity_per_buyer: val };
                                }
                              });
                              return updated;
                            });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Stock Totale (default)</Label>
                        <Input
                          type="number"
                          min="0"
                          placeholder="Illimitato"
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || undefined;
                            // Applica a tutti i prodotti selezionati
                            setSelectedProducts(prev => {
                              const updated = { ...prev };
                              Object.keys(updated).forEach(key => {
                                if (updated[key].selected) {
                                  updated[key] = { ...updated[key], max_quantity_total: val };
                                }
                              });
                              return updated;
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Shipping */}
            <div className="grid grid-cols-2 gap-4 border-t pt-4">
              <div className="space-y-2">
                <Label>Spedizione</Label>
                <Select
                  value={groupageForm.shipping_cost_type}
                  onValueChange={(v: 'included' | 'fixed' | 'per_order') => setGroupageForm({ ...groupageForm, shipping_cost_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="included">Inclusa nel prezzo</SelectItem>
                    <SelectItem value="fixed">Costo fisso totale</SelectItem>
                    <SelectItem value="per_order">Costo per ordine</SelectItem>
                  </SelectContent>
                </Select>
                {groupageForm.shipping_cost_type !== 'included' && (
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Costo spedizione (€)"
                    value={groupageForm.shipping_cost}
                    onChange={(e) => setGroupageForm({ ...groupageForm, shipping_cost: parseFloat(e.target.value) || 0 })}
                  />
                )}
              </div>
            </div>

            {/* Payment Notes */}
            <div className="space-y-2">
              <Label htmlFor="payment-notes">Note Pagamento</Label>
              <Textarea
                id="payment-notes"
                value={groupageForm.payment_notes}
                onChange={(e) => setGroupageForm({ ...groupageForm, payment_notes: e.target.value })}
                placeholder="es. Pagamento tramite bonifico entro 7 giorni dalla consegna"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateGroupageDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateGroupage} 
              disabled={creatingGroupage || !groupageForm.title || !groupageForm.seller_company_id}
            >
              {creatingGroupage ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Crea Groupage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssociationDashboardPage;
